import { ColumnDef } from '@tanstack/react-table';
import { Link, useNavigate } from 'react-router-dom';
import type { Invoice, InvoiceStatus, Client } from '@/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@/components/ui/data-table';
import { Checkbox } from '@/components/ui/checkbox';
import {
  MoreHorizontal,
  Eye,
  Pencil,
  Trash2,
  Copy,
  FileText,
  Download,
  Calendar,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface InvoicesTableProps {
  invoices: Invoice[];
  clients: Client[];
  isLoading?: boolean;
  updatingStatus?: string | null;
  updatingAccounting?: string | null;
  onDelete?: (invoice: Invoice) => void;
  onStatusChange?: (invoiceId: string, currentStatus: InvoiceStatus, newStatus: InvoiceStatus) => void;
  onToggleSentToAccounting?: (invoiceId: string, sentToAccounting: boolean) => void;
  onViewPdf?: (invoiceId: string) => void;
  onDownloadPdf?: (invoiceId: string, invoiceNumber: string) => void;
  emptyState?: React.ReactNode;
}

const statusConfig: Record<InvoiceStatus, { label: string; className: string }> = {
  draft: {
    label: 'Draft',
    className: 'border-muted-foreground text-muted-foreground',
  },
  sent: {
    label: 'Sent',
    className: 'border-blue-500 text-blue-500',
  },
  paid: {
    label: 'Paid',
    className: 'border-green-600 text-green-600',
  },
  overdue: {
    label: 'Overdue',
    className: 'border-red-500 text-red-500',
  },
  cancelled: {
    label: 'Cancelled',
    className: 'border-muted-foreground text-muted-foreground',
  },
};

export function InvoicesTable({
  invoices,
  clients,
  isLoading,
  updatingStatus,
  updatingAccounting,
  onDelete,
  onStatusChange,
  onToggleSentToAccounting,
  onViewPdf,
  onDownloadPdf,
  emptyState,
}: InvoicesTableProps) {
  const navigate = useNavigate();

  const getClientName = (clientId: string) => {
    const client = clients.find((c) => c.id === clientId);
    return client?.name || 'Unknown Client';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const columns: ColumnDef<Invoice>[] = [
    {
      accessorKey: 'invoiceNumber',
      header: 'Invoice #',
      cell: ({ row }) => {
        const invoice = row.original;
        return (
          <Link
            to={`/finances/invoices/${invoice.id}`}
            className="font-medium text-primary hover:underline"
          >
            {invoice.invoiceNumber}
          </Link>
        );
      },
    },
    {
      accessorKey: 'clientId',
      header: 'Client',
      cell: ({ row }) => {
        const clientId = row.getValue('clientId') as string;
        return <span className="text-foreground">{getClientName(clientId)}</span>;
      },
    },
    {
      accessorKey: 'issueDate',
      header: 'Issue Date',
      cell: ({ row }) => {
        const date = row.getValue('issueDate') as string;
        return (
          <div className="flex items-center gap-1.5 text-foreground">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span>{formatDate(date)}</span>
          </div>
        );
      },
    },
    {
      accessorKey: 'dueDate',
      header: 'Due Date',
      cell: ({ row }) => {
        const invoice = row.original;
        const isOverdue = invoice.status === 'overdue' ||
          (invoice.status !== 'paid' && new Date(invoice.dueDate) < new Date());
        return (
          <span className={cn('text-foreground', isOverdue && 'text-destructive font-medium')}>
            {formatDate(invoice.dueDate)}
          </span>
        );
      },
    },
    {
      accessorKey: 'total',
      header: 'Amount',
      cell: ({ row }) => {
        const invoice = row.original;
        return (
          <div>
            <div className="font-semibold text-foreground">
              {formatCurrency(invoice.total)}
            </div>
            {invoice.paidAmount > 0 && invoice.status !== 'paid' && (
              <div className="text-xs text-muted-foreground">
                Paid: {formatCurrency(invoice.paidAmount)}
              </div>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const invoice = row.original;
        const isUpdating = updatingStatus === invoice.id;
        const config = statusConfig[invoice.status];

        if (onStatusChange) {
          return (
            <div className="flex items-center gap-2">
              <Select
                value={invoice.status}
                onValueChange={(value) =>
                  onStatusChange(invoice.id, invoice.status, value as InvoiceStatus)
                }
                disabled={isUpdating}
              >
                <SelectTrigger className="w-[130px] h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              {isUpdating && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
            </div>
          );
        }

        return (
          <Badge className={config.className}>
            {config.label}
          </Badge>
        );
      },
    },
    {
      id: 'sentToAccounting',
      header: 'Sent to Accounting',
      cell: ({ row }) => {
        const invoice = row.original;
        const isUpdating = updatingAccounting === invoice.id;
        const checked = !!invoice.sentToAccounting;

        return (
          <div className="flex items-center gap-2">
            <Checkbox
              checked={checked}
              disabled={isUpdating || !onToggleSentToAccounting}
              onChange={(e) => onToggleSentToAccounting?.(invoice.id, e.target.checked)}
              aria-label={`Mark invoice ${invoice.invoiceNumber} as sent to accounting`}
            />
            {isUpdating && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          </div>
        );
      },
    },
    {
      id: 'actions',
      cell: ({ row }) => {
        const invoice = row.original;
        const canEdit = invoice.status === 'draft' || invoice.status === 'sent';
        const canDelete = invoice.status === 'draft';
        const canViewPdf = invoice.status === 'sent' || invoice.status === 'paid';

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreHorizontal className="h-4 w-4" />
                <span className="sr-only">Open menu</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate(`/finances/invoices/${invoice.id}`)}>
                <Eye className="mr-2 h-4 w-4" />
                View
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(`/finances/invoices/new?duplicateFrom=${invoice.id}`)}>
                <Copy className="mr-2 h-4 w-4" />
                Duplicate
              </DropdownMenuItem>
              {canEdit && (
                <DropdownMenuItem onClick={() => navigate(`/finances/invoices/${invoice.id}/edit`)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Edit
                </DropdownMenuItem>
              )}
              {canViewPdf && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => onViewPdf?.(invoice.id)}>
                    <FileText className="mr-2 h-4 w-4" />
                    View PDF
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onDownloadPdf?.(invoice.id, invoice.invoiceNumber)}>
                    <Download className="mr-2 h-4 w-4" />
                    Download PDF
                  </DropdownMenuItem>
                </>
              )}
              {canDelete && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => onDelete?.(invoice)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={invoices}
      isLoading={isLoading}
      emptyState={emptyState}
      pageSize={10}
    />
  );
}
